import { mediaSource } from '../shared/mediaSource';
import MediaImage from './MediaImage';
import { useEffect, useRef, useState } from 'react';
import cdoAI from '../cdo/cdoAI.js';
import cdoBeing from '../cdo/cdoBeing.js';

const emptyImage = () => ({ src: '', revisedPrompt: '', isSaved: false, hasCheckedSavedImage: false });
const request = (method, ...args) => new Promise((resolve, reject) => method(...args, (error, response) => {
  if (error || response?.error) reject(new Error(response?.error?.message || response?.error || error?.message || 'Could not complete image request.'));
  else resolve(response);
}));
export const buildBeingTemplateImagePrompt = (template, masterTypes) => {
  const lookup = (column, id) => masterTypes.find(row => row.table === 'being' && row.column === column && String(row.idMasterType) === String(id))?.value || 'Unspecified';
  return `Create a square 1:1 fantasy illustration of a representative ${template.name || 'being'} from this Adenrel Being Template.
Being type: ${lookup('beingType', template.beingType)}
Default form: ${template.defaultForm || 'Unspecified'}
Description: ${template.description || 'No description provided.'}
Habitat: ${template.habitat || 'Unspecified'}
Alignment: ${lookup('alignment', template.alignment)}
Heraldry: ${template.heraldry || 'None'}
Length / height: ${template.length_min || 'unknown'}–${template.length_max || 'unknown'} inches.
Weight: ${template.weight_min || 'unknown'}–${template.weight_max || 'unknown'} pounds.
Wingspan / armspan: ${template.wingspan_min || 'unknown'}–${template.wingspan_max || 'unknown'} inches.
Visual requirements:
- Follow the supplied anatomy and description. Choose one coherent representative within the size ranges.
- Show the being clearly as the main subject with its distinctive anatomy visible.
- Use habitat as background inspiration. Do not portray statistical ranges, objectives or encounter notes as literal imagery.
- Compose for a square 1:1 image without text, captions, UI, logos, watermarks or labels.`;
};

export default function BeingTemplateImage({ template, masterTypes = [], aogYear = 0 }) {
  const [image, setImage] = useState(emptyImage);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const version = useRef(0);
  useEffect(() => {
    const current = ++version.current;
    setImage(emptyImage());
    setBusy(template.idEntity ? 'load' : '');
    setMessage('');
    if (template.idEntity) request(cdoBeing.selectPortraitImage, template.idEntity).then(saved => {
      if (current !== version.current) return;
      setImage(mediaSource(saved)
        ? { src: mediaSource(saved), revisedPrompt: saved.revisedPrompt || '', isSaved: true, hasCheckedSavedImage: true }
        : { ...emptyImage(), hasCheckedSavedImage: true });
    }).catch(error => { if (current === version.current) setMessage(error.message); })
      .finally(() => { if (current === version.current) setBusy(''); });
    return () => { version.current += 1; };
  }, [template.idEntity]);
  const generate = async () => {
    const current = ++version.current;
    const shouldAutoSaveGeneratedImage = image.hasCheckedSavedImage && !image.src;
    setBusy('generate');
    setMessage('');
    try {
      const response = await request(cdoAI.generateImage, { aogYear: Number(aogYear) || 0, size: '1024x1024', prompt: buildBeingTemplateImagePrompt(template, masterTypes) });
      if (current !== version.current) return;
      if (!response?.imageBase64) throw new Error('Image generation returned no image data.');
      const generatedImage = { src: `data:${response.mimeType || 'image/png'};base64,${response.imageBase64}`, revisedPrompt: response.revisedPrompt || '', isSaved: false, hasCheckedSavedImage: true };
      setImage(generatedImage);
      if (shouldAutoSaveGeneratedImage) {
        await request(cdoBeing.savePortraitImage, {
          idEntity: template.idEntity,
          name: template.name,
          title: template.title || template.name,
          mediaType: response.mimeType || 'image/png',
          imageBase64: response.imageBase64,
          revisedPrompt: response.revisedPrompt || '',
        });
        if (current !== version.current) return;
        setImage({ ...generatedImage, isSaved: true });
        setMessage('Generated and saved template image.');
      } else {
        setMessage('Generated template image.');
      }
    } catch (error) { if (current === version.current) setMessage(error.message); }
    finally { if (current === version.current) setBusy(''); }
  };
  const save = async () => {
    const current = ++version.current;
    setBusy('save');
    setMessage('');
    try {
      const match = image.src.match(/^data:([^;]+);base64,(.+)$/);
      if (!match) throw new Error('Generated image data is not in a savable format.');
      await request(cdoBeing.savePortraitImage, { idEntity: template.idEntity, name: template.name, title: template.title || template.name, mediaType: match[1], imageBase64: match[2], revisedPrompt: image.revisedPrompt });
      if (current !== version.current) return;
      setImage(previous => ({ ...previous, isSaved: true }));
      setMessage('Saved template image.');
    } catch (error) { if (current === version.current) setMessage(error.message); }
    finally { if (current === version.current) setBusy(''); }
  };
  return <section className="editor-section being-portrait-section template-wide">
    <div className="being-portrait-copy"><h3>Image</h3>{message && <div className="panel-status" role="status">{message}</div>}</div>
    <div className="being-portrait-panel"><div className="being-portrait-frame">{image.src ? <MediaImage src={image.src} alt={`${template.name || 'Being Template'} illustration`} /> : <span>{template.idEntity ? 'No image saved' : 'Save template first'}</span>}</div>
      <div className="being-portrait-actions">
        <button type="button" className="secondary-button" disabled={!template.idEntity || Boolean(busy)} onClick={generate}>{busy === 'load' ? 'Loading...' : busy === 'generate' ? 'Generating...' : 'Generate Image'}</button>
        <button type="button" className="secondary-button" disabled={!template.idEntity || !image.src || image.isSaved || Boolean(busy)} onClick={save}>{busy === 'save' ? 'Saving...' : 'Save Image'}</button>
      </div>
    </div>
  </section>;
}
